import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-scroll";
import { FaLinkedin, FaGithub } from "react-icons/fa";

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
};

const empresas = [
  { name: "Grupo Boticário", img: "/empresas/a.png", descricao: "Analista DevOps Sr.", periodo: "2023 - Atual" },
  { name: "iFood", img: "/empresas/b.png", descricao: "Engenheiro de Software Sr.", periodo: "2022 - 2023" },
  { name: "PagSeguro", img: "/empresas/c.png", descricao: "Site Reliability Engineer Sr.", periodo: "2020 - 2022" },
  { name: "Maplink", img: "/empresas/d.png", descricao: "Analista de Infraestrutura.", periodo: "2015 - 2020" },
  { name: "Apontador", img: "/empresas/e.png", descricao: "Analista de Infraestrutura.", periodo: "2015 - 2020" },
];

function App() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showFooter, setShowFooter] = useState(true);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);

  const menuRef = useRef(null); // referência para o menu e botão

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.body.offsetHeight;
      setShowFooter(scrollTop + windowHeight > docHeight - 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fecha o menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="bg-gray-900 text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full bg-gray-800 bg-opacity-90 shadow z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center p-4">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer">
            <svg className="w-14 h-14" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="48" fill="#1E3A8A" />
              <path d="M30 70 L30 30 L55 30 L55 40 L40 40 L40 70 Z" fill="#60A5FA" />
              <path d="M60 70 L60 30 L70 30 L70 70 Z" fill="#93C5FD" />
              <circle cx="75" cy="25" r="2" fill="#FACC15" />
              <circle cx="80" cy="30" r="2" fill="#FACC15" />
              <line x1="75" y1="25" x2="80" y2="30" stroke="#FACC15" strokeWidth="1" />
            </svg>
            <div className="flex flex-col">
              <span className="font-logo text-2xl md:text-3xl transition transform hover:scale-105 hover:text-blue-400">
                Ricardo Lima
              </span>
              <span className="font-site text-xs text-gray-300">rlamaral.com.br</span>
            </div>
          </div>

          {/* Menu + Dropdown */}
          <div className="relative menu-dropdown" ref={menuRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="menu-button flex items-center gap-2 px-4 py-2 bg-blue-500 rounded hover:bg-blue-600 transition"
            >
              {/* Ícone hambúrguer */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span>Menu</span>
            </button>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  className="absolute right-0 mt-2 w-48 bg-gray-700 bg-opacity-80 backdrop-blur-sm rounded shadow-lg overflow-hidden"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <Link to="sobre" smooth={true} duration={800} className="block px-4 py-2 hover:bg-gray-600 hover:bg-opacity-70 transition" onClick={() => setDropdownOpen(false)}>Sobre</Link>
                  <Link to="experiencia" smooth={true} duration={800} className="block px-4 py-2 hover:bg-gray-600 hover:bg-opacity-70 transition" onClick={() => setDropdownOpen(false)}>Experiência</Link>
                  <Link to="contato" smooth={true} duration={800} className="block px-4 py-2 hover:bg-gray-600 hover:bg-opacity-70 transition" onClick={() => setDropdownOpen(false)}>Contato</Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      <div className="h-20" />

      {/* Sobre */}
      <section id="sobre" className="min-h-[60vh] flex flex-col items-center justify-center py-12 px-6">
        <motion.h1 className="text-5xl font-bold mb-6 text-center" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeInUp} transition={{ duration: 0.8 }}>
          Olá, sou o Ricardo!
        </motion.h1>
        <motion.p className="text-lg max-w-xl text-center" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeInUp} transition={{ duration: 0.8, delay: 0.2 }}>
          Profissional de Tecnologia da Informação, atuando desde 2007. Atualmente no Grupo Boticário como Analista DevOps Sênior, com foco em automação, confiabilidade e escalabilidade de sistemas. Experiência sólida em Cloud (AWS e GCP), Kubernetes, CI/CD e monitoramento de ambientes críticos.
        </motion.p>
      </section>

      {/* Experiência */}
      <section id="experiencia" className="min-h-[60vh] flex flex-col items-center justify-center py-12 px-6 bg-gray-800">
        <motion.h2 className="text-4xl font-semibold mb-6 text-center" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeInUp} transition={{ duration: 0.8 }}>
          Experiência
        </motion.h2>
        <motion.p className="text-center max-w-xl mb-8 text-gray-300" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeInUp} transition={{ duration: 0.8, delay: 0.2 }}>
          Abaixo algumas empresas do meu portfólio profissional:
        </motion.p>
        <motion.div className="flex justify-center items-center gap-8 flex-wrap" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}>
          {empresas.map((empresa, index) => (
            <motion.div
              key={index}
              className="w-36 h-36 flex items-center justify-center p-4 bg-white rounded-2xl shadow-md cursor-pointer"
              whileHover={{ scale: 1.1, filter: "brightness(1.2)" }}
              transition={{ duration: 0.3 }}
              onClick={() => setSelectedEmpresa(empresa)}
            >
              <img src={empresa.img} alt={`Logo da ${empresa.name}`} loading="lazy" className="max-h-28 object-contain" />
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedEmpresa && (
          <motion.div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedEmpresa(null)}>
            <motion.div className="relative bg-gray-900 bg-opacity-90 rounded-3xl p-8 max-w-sm w-full shadow-xl border border-blue-700" initial={{ opacity: 0, scale: 0.8, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.8, y: 20 }} transition={{ duration: 0.35, ease: "easeOut" }} onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-col items-center gap-4">
                <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center shadow-lg">
                  <img src={selectedEmpresa.img} alt={`Logo da ${selectedEmpresa.name}`} className="max-h-16 object-contain rounded-full" />
                </div>
                <h3 className="text-2xl font-bold">{selectedEmpresa.name}</h3>
                <p className="text-center text-gray-300">{selectedEmpresa.descricao}</p>
                <p className="text-center text-gray-400 text-sm italic">{selectedEmpresa.periodo}</p>
                <button onClick={() => setSelectedEmpresa(null)} className="mt-4 px-5 py-2 bg-blue-500 rounded-full hover:bg-blue-600 transition shadow-md">
                  Fechar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contato */}
      <section id="contato" className="min-h-[60vh] flex flex-col items-center justify-center py-12 px-6">
        <motion.h2 className="text-4xl font-semibold mb-6 text-center" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeInUp} transition={{ duration: 0.8 }}>
          Contato
        </motion.h2>
        <motion.p className="text-center max-w-md mb-6" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeInUp} transition={{ duration: 0.8, delay: 0.2 }}>
          Você pode me encontrar nas minhas redes profissionais:
        </motion.p>
        <motion.div className="flex gap-8 text-4xl" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeInUp} transition={{ duration: 0.8, delay: 0.4 }}>
          <a href="https://linkedin.com/in/ricalima" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 transition">
            <FaLinkedin />
          </a>
          <a href="https://github.com/ricardolima87" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition">
            <FaGithub />
          </a>
        </motion.div>
      </section>

      {/* Footer */}
      <AnimatePresence>
        {showFooter && (
          <motion.footer className="fixed bottom-0 left-0 w-full bg-gray-800 text-gray-400 text-center py-2 shadow-inner" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            {new Date().getFullYear()} - rlamaral.com.br
          </motion.footer>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
